import { Upload } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib';

type DropzonePresenterProps = {
  isDragOver: boolean;
  isDisabled: boolean;
  acceptedFileTypes: string[];
  maxFileSize: number;
  maxFiles: number;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onFileInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
};

export const DropzonePresenter: FC<DropzonePresenterProps> = ({
  isDragOver,
  isDisabled,
  acceptedFileTypes,
  maxFileSize,
  maxFiles,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileInput,
  className,
}) => {
  const { t } = useTranslation(['records']);

  return (
    <div
      className={cn(
        'relative flex min-h-[200px] items-center justify-center rounded-xl border-2 border-dashed p-8 text-center transition-all duration-200',
        isDisabled
          ? 'border-muted bg-muted/30 cursor-not-allowed opacity-60'
          : isDragOver
            ? 'border-primary bg-primary/5 shadow-md'
            : 'border-border hover:border-primary/50 hover:bg-muted/50',
        className,
      )}
      onDragOver={isDisabled ? undefined : onDragOver}
      onDragLeave={isDisabled ? undefined : onDragLeave}
      onDrop={isDisabled ? undefined : onDrop}
    >
      <input
        type="file"
        multiple
        accept={acceptedFileTypes.join(',')}
        onChange={onFileInput}
        disabled={isDisabled}
        className={cn('absolute inset-0 opacity-0', isDisabled ? 'cursor-not-allowed' : 'cursor-pointer')}
        aria-label={t('records:fileUpload.selectFiles')}
        aria-disabled={isDisabled}
      />

      <div className="space-y-4">
        <div
          className={cn(
            'mx-auto flex h-16 w-16 items-center justify-center rounded-full transition-all',
            isDisabled
              ? 'bg-muted/50 text-muted-foreground'
              : isDragOver
                ? 'scale-105 bg-primary/10 text-primary'
                : 'bg-muted text-muted-foreground',
          )}
        >
          <Upload className="h-8 w-8" />
        </div>

        <div className="space-y-1">
          <p className={cn('text-lg font-medium', isDisabled ? 'text-muted-foreground' : 'text-foreground')}>
            {isDisabled ? t('records:fileUpload.maxFilesReached', { maxFiles }) : t('records:fileUpload.dragAndDrop')}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('records:fileUpload.fileInfo', {
              types: acceptedFileTypes.join(', '),
              maxSize: maxFileSize,
              filesCount: t('records:fileUpload.filesCount', { count: maxFiles }),
            })}
          </p>
        </div>
      </div>
    </div>
  );
};
